import { Pencil, Trash2 } from 'lucide-react';
import type { AuthoringQuestion, AuthoringQuestionType } from '../../../school_panel/api/types';
import { TYPE_ICON, TYPE_LABEL } from './QuestionTypePicker';

const plain = (s: string) => s.replace(/<[^>]+>/g, ' ').replace(/\[\[(\d+)\]\]/g, '____').replace(/\s+/g, ' ').trim();

export default function QuestionList({ questions, activeId, problemIds, onEdit, onDelete }: {
  questions: AuthoringQuestion[];
  activeId?: number | null;
  problemIds?: Set<number>;
  onEdit: (q: AuthoringQuestion) => void;
  onDelete: (q: AuthoringQuestion) => void;
}) {
  if (!questions.length) {
    return <p className="rounded-[14px] border border-dashed border-line-strong p-4 text-sm text-muted">No questions yet. Add one, or import them from a file with AI.</p>;
  }
  return (
    <ol className="space-y-1.5" aria-label="Questions">
      {questions.map((q, i) => {
        const on = activeId === q.id;
        const t = q.type as AuthoringQuestionType;
        return (
          <li key={q.id} className={`group flex items-start gap-2.5 rounded-[14px] border px-3 py-2.5 ${on ? 'border-navy bg-note' : 'border-line bg-surface hover:border-line-strong'}`}>
            <span className="mt-0.5 w-7 flex-none font-mono text-xs font-semibold text-muted">Q{i + 1}</span>
            <button type="button" onClick={() => onEdit(q)} className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              <span className="line-clamp-2 break-words text-[13px] text-ink">{plain(q.text) || '(no text)'}</span>
              <span className="mt-1 flex items-center gap-1.5 text-xs text-muted">
                <span className="text-slate">{TYPE_ICON[t]}</span>{TYPE_LABEL[t] ?? q.type}
                {q.type === 'written' && q.max_marks ? <span>· {q.max_marks} marks</span> : null}
                {problemIds?.has(q.id) && <span className="font-semibold text-danger-ink">· needs a fix</span>}
              </span>
            </button>
            <span className="flex flex-none gap-0.5">
              <button type="button" aria-label={`Edit Q${i + 1}`} onClick={() => onEdit(q)}
                className="grid h-8 w-8 place-items-center rounded-[9px] text-muted hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"><Pencil size={15} aria-hidden /></button>
              <button type="button" aria-label={`Delete Q${i + 1}`} onClick={() => onDelete(q)}
                className="grid h-8 w-8 place-items-center rounded-[9px] text-muted hover:bg-danger-bg hover:text-danger-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"><Trash2 size={15} aria-hidden /></button>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
